import { Component, Suspense, createRef } from 'react';

import clone from 'lodash/clone';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import { Box } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';

import FiltersComponent from './FiltersComponent';
import MultiFilterSelectComponent from './MultiFilterSelectComponent';
import PaginationComponent from './PaginationComponent';
import RecordsInfoComponent from './RecordsInfoComponent';
import SearchComponent from './SearchComponent';
import TableComponent from './TableComponent';

import { getSelectValue } from '../../utils';
import { lazyImport } from '../Main/utils';
import {
  formatQueryParams,
  getDateRangeFilterDate,
  getDefaultParams,
  getInitialParams,
  getSkeletonColumns,
  getSkeletonEntries,
} from './utils';

import { DATE_RANGES_TYPES } from '../../staticData';
import { history } from '../../store';
import ErrorBoundary from '../Main/ErrorBoundary';
import { dateRangeTypeFilter } from './filters';

const CalendarContainer = lazyImport(
  () => import('../Calendar/CalendarContainer'),
  'CalendarContainer',
);
const DateRangePickerWrapper = lazyImport(
  () => import('../Forms/DateRangePicker'),
  'DateRangePicker',
);

class AsyncTableComponent extends Component {
  static getDerivedStateFromProps(props, state) {
    const { dateRangeTypeOptions } = props;

    // we need this to check if there are options to choose from.
    // if there are not, { value: dateRangeType } is returned
    // cause in this class dateRangeType expected to have the value property
    const hasOptions = dateRangeTypeOptions?.length > 0;

    if (!state.dateRangeType) {
      const historySearch = get(history, 'location.search', null);
      const search = queryString.parse(historySearch);

      let dateRangeType;

      Object.entries(search).forEach(([key, value]) => {
        if (value) {
          Object.values(DATE_RANGES_TYPES).forEach((typeValue) => {
            if (key.includes(typeValue)) {
              dateRangeType = typeValue;
            }
          });
        }
      });

      if (dateRangeType) {
        dateRangeType = hasOptions
          ? getSelectValue(dateRangeType, dateRangeTypeOptions)
          : { value: dateRangeType };
      } else {
        dateRangeType = hasOptions
          ? getSelectValue(props?.dateRangeType, dateRangeTypeOptions)
          : { value: props?.dateRangeType };
      }

      return { dateRangeType };
    }
    return null;
  }

  constructor(props) {
    super(props);
    this.state = {
      dateRangeType: null,
      startDate: null,
      endDate: null,
    };
    const { defaultParams } = this.props;
    this.searchInput = createRef();
    this.searchChanged = false;

    this.onOrderList = this.onOrderList.bind(this);
    this.onFilterChange = this.onFilterChange.bind(this);
    this.onSortedChange = this.onSortedChange.bind(this);
    this.removeFilters = this.removeFilters.bind(this);
    this.checkInitialDates = this.checkInitialDates.bind(this);
    this.onQueryChange = debounce(this.onQueryChange, 800).bind(this);

    this.initialParams = getInitialParams(getDefaultParams(defaultParams));
  }

  componentDidMount() {
    const { onLoad, setListParams, title, defaultParams, match } = this.props;
    const buildingId = match?.params?.buildingId;
    const search = get(history, 'location.search', null);

    if (title && typeof title === 'string') {
      document.title = title;
    }

    if (search) {
      const params = formatQueryParams(queryString.parse(search), buildingId);
      const stringifiedParams = queryString.stringify(params);

      AsyncTableComponent.setSearchValue(stringifiedParams);
      setListParams(params);
      this.checkInitialDates(params);
    } else {
      const params = formatQueryParams(this.initialParams, buildingId);
      this.checkInitialDates(defaultParams);
      setListParams(params);
    }
    if (onLoad) {
      onLoad();
    }
  }

  shouldComponentUpdate(nextProps) {
    if (nextProps.shouldUpdate === false) {
      return false;
    }
    return true;
  }

  componentDidUpdate(prevProps) {
    const {
      getItems,
      listParams,
      showWithSearchOnly,
      isCustomModalShown,
      searchAutoFocus,
      isCalendarView,
      defaultParams,
      setListParams,
    } = this.props;
    const search = listParams?.q ?? '';
    const prevSearch = prevProps?.listParams?.q ?? '';
    if (search !== prevSearch) {
      this.searchChanged = true;
    } else if (
      this.searchInput?.current &&
      this.searchChanged &&
      !this.searchInput?.current.disabled
    ) {
      searchAutoFocus && this.searchInput?.current.focus();
      this.searchChanged = false;
    }
    if (!isCalendarView && prevProps.isCalendarView) {
      setListParams(defaultParams);
    }
    if (
      !isEmpty(listParams) &&
      JSON.stringify(listParams) !== JSON.stringify(prevProps.listParams)
    ) {
      const stringified = queryString.stringify(listParams);
      if (!stringified) {
        setListParams(this.initialParams);
        // TO DO: this is ugly, we shouldn't have to mutate elements directly
        // however as we're using debounce when searching it makes it
        // difficult to have the input feel responsive while bound to redux store's state.
        // Binding to the component state might make sense
        // this.setState({search: ''})
        // this.searchInput.value = '';
      }
      if (window.history.pushState && stringified) {
        AsyncTableComponent.setSearchValue(stringified);
        if (showWithSearchOnly && search) {
          getItems(listParams);
        }
        if (!showWithSearchOnly) {
          getItems(listParams);
        }
      }
    }
    if (!isCustomModalShown && prevProps.isCustomModalShown) {
      getItems(listParams);
    }
    if (
      this.searchInput?.current &&
      search !== this.searchInput?.current.value
    ) {
      this.searchInput.current.value = search;
    }
  }

  componentWillUnmount() {
    const { onUnmount } = this.props;

    if (onUnmount) {
      onUnmount();
    }
  }

  onOrderList(ordering) {
    const { setListParams } = this.props;
    if (ordering === get(this.props, 'listParams.ordering')) {
      setListParams({
        ordering: `-${ordering}`,
        offset: 0,
      });
    } else {
      setListParams({
        ordering,
        offset: 0,
      });
    }
  }

  static setSearchValue(search) {
    const { protocol, host, pathname } = window.location;
    const newUrl = `${protocol}//${host}${pathname}?${search}`;
    window.history.replaceState({ path: newUrl }, '', newUrl);
  }

  onFilterChange(data) {
    // only update state if the filter actually changed
    const { listParams, setListParams, optimizeParams } = this.props;
    let changed = false;
    let params = null;
    Object.entries(data).forEach(([key, val]) => {
      if (listParams[key] !== val) {
        changed = true;
      }
    });
    if (changed) {
      params = { ...data, offset: 0 };
    }
    if (changed && optimizeParams) {
      params = optimizeParams(params, listParams);
    }
    params && setListParams(params);
  }

  onSortedChange(params) {
    const isManual = get(this.props, 'manual');
    if (isManual === false) return;
    const listParams = get(this.props, 'listParams');
    const { defaultSortDesc = false, setListParams } = this.props;
    const isColumnSortOrderDesc = defaultSortDesc
      ? !get(params, '[0].desc')
      : get(params, '[0].desc');
    const order = isColumnSortOrderDesc ? '-' : '';
    const value = get(params, '[0].id');
    setListParams({
      ...listParams,
      offset: 0,
      ordering: `${order}${value}`,
    });
  }

  onDateRangeChange = ({ startDate, endDate }, params = {}, useDefaultTime) => {
    const { dateRangeType } = this.state;
    const dateRangeTypeValue = dateRangeType?.value;

    const startDateType = dateRangeTypeValue && `${dateRangeTypeValue}_after`;
    const endDateType = dateRangeTypeValue && `${dateRangeTypeValue}_before`;

    const date = {
      [startDateType]: getDateRangeFilterDate(startDate, true, useDefaultTime),
      [endDateType]: getDateRangeFilterDate(endDate, false, useDefaultTime),
    };
    this.setState({
      startDate,
      endDate,
    });
    return this.onFilterChange({ ...params, ...date });
  };

  onDateRangeTypeChange = async (data, updateStateOnly) => {
    const { dateRangeType } = this.state;
    const {
      listParams: params,
      dateRangeTypeOptions,
      setListParams,
    } = this.props;

    const listParams = clone(params);
    const prevDateRangeType = dateRangeType?.value;
    Object.keys(listParams).forEach((param) => {
      if (param.includes(prevDateRangeType)) {
        listParams[param] = null;
      }
    });
    const newDateRangeType = getSelectValue(
      data?.dateRangeType,
      dateRangeTypeOptions,
    );
    await this.setState(
      {
        dateRangeType: newDateRangeType,
      },
      () => (!updateStateOnly ? setListParams({ ...listParams }) : null),
    );
    return listParams;
  };

  onGetTodayRecords = async (data) => {
    const { dateRangeType } = this.props;
    const params = await this.onDateRangeTypeChange({ dateRangeType }, true);
    return this.onDateRangeChange(data, params);
  };

  onQueryChange(e) {
    const { isLoading, setListParams } = this.props;
    !isLoading &&
      setListParams({
        q: e.target.value || '',
        offset: 0,
      });
  }

  onPageChange = (event, page) => {
    const { listParams, setListParams } = this.props;
    const { limit } = listParams;
    const offset = (page - 1) * limit;
    setListParams({ limit, offset });
  };

  checkInitialDates(search) {
    const {
      dateRangeType: dateRangeTypeState,
      startDate: startDateState,
      endDate: endDateState,
    } = this.state;
    const dateRangeType = dateRangeTypeState?.value;
    const startDateType = dateRangeType && `${dateRangeType}_after`;
    const endDateType = dateRangeType && `${dateRangeType}_before`;
    const startDate = get(search, startDateType, null);
    const endDate = get(search, endDateType, null);
    if (
      (startDate && startDateState !== startDate) ||
      (endDate && endDateState !== endDate)
    ) {
      this.setState({
        startDate: startDate && moment(startDate),
        endDate: endDate && moment(endDate),
      });
    }
  }

  removeFilters() {
    const { setListParams } = this.props;
    setListParams(this.initialParams);
    if (this.searchInput?.current.value) {
      this.searchInput.current.value = '';
    }
  }

  render() {
    const {
      isLoading,
      tableId,
      items,
      listParams,
      columns,
      manual,
      defaultSort,
      defaultSortDesc,
      filters,
      action,
      isHideSearch,
      customActions,
      CustomInfoComponent,
      datePicker,
      dateRangeTypeOptions,
      tableDataType,
      searchPlaceholder,
      shouldShowItems = true,
      onRowClick,
      rowStyles,
      buildingLaunchDate,
      exportActions,
      contextMenuId,
      usersCount,
      todayCount,
      showWithSearchOnly,
      hideRecordsInfo = false,
      noBorders,
      showTableRowsOnly,
      isDrawerOpen,
      isCalendarView,
      chartMetadata,
      withTimePicker,
      SubComponent,
      searchAutoFocus,
      isMobileScreen,
      hasToggleFilters,
      expandedByDefault,
    } = this.props;

    const results = shouldShowItems ? items?.results : [];
    const count = get(items, 'count');
    const {
      dateRangeType,
      startDate: startDateState,
      endDate: endDateState,
    } = this.state;
    const dateRangeTypeValue = dateRangeType?.value;
    const startDateType = dateRangeTypeValue && `${dateRangeTypeValue}_after`;
    const endDateType = dateRangeTypeValue && `${dateRangeTypeValue}_before`;
    const startDate = get(listParams, `${startDateType}`, null);
    const endDate = get(listParams, `${endDateType}`, null);
    const isDateSelected = !!startDate || !!endDate;
    const hasItems = results?.length >= 0;
    const shouldShowRecordsInfo = hasItems && !hideRecordsInfo;
    const shouldHideTable =
      showWithSearchOnly &&
      (!this.searchInput?.current || !this.searchInput?.current.value);

    const { limit = 25, offset, ordering } = listParams;

    const currentPage = offset ? offset / limit + 1 : 1;

    const totalPages = Math.ceil(get(items, 'count', 0) / limit);

    const SearchField = (
      <SearchComponent
        placeholder={searchPlaceholder}
        onChange={this.onQueryChange}
        defaultValue={this.initialParams?.q ?? ''}
        disabled={isLoading}
        inputRef={this.searchInput}
        searchAutoFocus={searchAutoFocus}
        showTableRowsOnly={showTableRowsOnly}
      />
    );

    const DateRangeComponent = (
      <Box display="flex" alignItems="flex-start" flexWrap="wrap" gap="1rem">
        {Boolean(datePicker && dateRangeTypeValue) && (
          <Suspense
            fallback={
              <Skeleton
                variant="rounded"
                width={300}
                height={40}
                sx={{ borderRadius: '1rem' }}
              />
            }
          >
            <DateRangePickerWrapper
              onDateRangeChange={this.onDateRangeChange}
              isDateSelected={isDateSelected}
              startDate={startDateState}
              endDate={endDateState}
              isLoading={isLoading}
              withTimePicker={withTimePicker}
              isMobileScreen={isMobileScreen}
            />
          </Suspense>
        )}
        {dateRangeTypeOptions?.length > 0 && (
          <Box mr="1rem" width="15rem">
            <MultiFilterSelectComponent
              filters={[
                dateRangeTypeFilter(dateRangeTypeOptions, dateRangeTypeValue),
              ]}
              onChange={this.onDateRangeTypeChange}
            />
          </Box>
        )}
      </Box>
    );

    const RecordsComponent = (
      <RecordsInfoComponent
        count={count}
        todayCount={todayCount}
        tableDataType={tableDataType}
        launchDate={buildingLaunchDate}
        isDateSelected={isDateSelected}
        startDate={startDate}
        endDate={endDate}
        usersCount={usersCount}
        hasFilterByDate={Boolean(datePicker && dateRangeTypeValue)}
        onDateRangeChange={this.onGetTodayRecords}
      />
    );

    const showSkeleton = !hasItems;
    const formattedColumns = showSkeleton
      ? getSkeletonColumns(columns)
      : columns;
    const formattedEntries = showSkeleton
      ? getSkeletonEntries(limit)
      : results || [];

    return (
      <Box width="100%" display="flex" flexDirection="column">
        <FiltersComponent
          filters={filters}
          onChange={this.onFilterChange}
          action={action}
          SearchComponent={!isHideSearch && SearchField}
          DateRangeComponent={DateRangeComponent}
          showTableRowsOnly={showTableRowsOnly}
          chartMetadata={chartMetadata}
          isLoading={isLoading}
          hasToggleFilters={hasToggleFilters}
        />

        {shouldHideTable || isCalendarView ? null : (
          <>
            <Box display="flex" alignItems="center">
              {showSkeleton ? (
                <Skeleton width={200} height={24} />
              ) : (
                <Box
                  width="100%"
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  {!!shouldShowRecordsInfo && RecordsComponent}
                  {!!CustomInfoComponent && CustomInfoComponent}
                </Box>
              )}
            </Box>

            <TableComponent
              tableId={tableId}
              entries={formattedEntries}
              columns={formattedColumns}
              manual={manual}
              defaultSort={defaultSort}
              defaultSortDesc={defaultSortDesc}
              ordering={ordering}
              onOrderList={this.onOrderList}
              sortFunction={this.onSortedChange}
              onRowClick={onRowClick}
              rowStyles={rowStyles}
              contextMenuId={contextMenuId}
              noBorders={noBorders}
              SubComponent={SubComponent}
              isMobileScreen={isMobileScreen}
              expandedByDefault={expandedByDefault}
            />
            {customActions}

            <Box display="flex" justifyContent="space-between" my={1}>
              {results && results.length ? (
                <>
                  <Box>{exportActions}</Box>
                  <PaginationComponent
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onChange={this.onPageChange}
                    isMobileScreen={isMobileScreen}
                  />
                </>
              ) : null}
            </Box>
          </>
        )}
        {isCalendarView && (
          <Suspense
            fallback={
              <Skeleton
                variant="rounded"
                width="100%"
                height="50vh"
                sx={{ borderRadius: '1rem' }}
              />
            }
          >
            <ErrorBoundary>
              <CalendarContainer
                onFilterChange={this.onFilterChange}
                isDrawerOpen={isDrawerOpen}
                listParams={listParams}
              />
            </ErrorBoundary>
          </Suspense>
        )}
      </Box>
    );
  }
}

AsyncTableComponent.defaultProps = {
  onLoad: () => {},
  onUnmount: () => {},
  title: null,
  filters: null,
  action: null,
  isHideSearch: null,
  hideRecordsInfo: false,
  noBorders: false,
  defaultParams: null,
  dateRangeTypeOptions: [],
  dateRangeType: null,
  showWithSearchOnly: false,
  isCustomModalShown: false,
  isCalendarView: false,
  defaultSortDesc: false,
  optimizeParams: null,
  shouldShowItems: true,
  datePicker: false,
  rowStyles: {},
  exportActions: null,
  contextMenuId: '',
  todayCount: null,
  usersCount: null,
  CustomInfoComponent: null,
  SubComponent: null,
  withTimePicker: false,
  chartMetadata: null,
  showTableRowsOnly: false,
  shouldUpdate: null,
  customActions: null,
  onRowClick: null,
  tableDataType: undefined,
  buildingLaunchDate: null,
  hasToggleFilters: true,
};

AsyncTableComponent.propTypes = {
  tableId: PropTypes.string.isRequired,
  items: PropTypes.instanceOf(Object).isRequired,
  match: PropTypes.instanceOf(Object).isRequired,
  columns: PropTypes.instanceOf(Array).isRequired,
  filters: PropTypes.instanceOf(Array),
  onLoad: PropTypes.func,
  onUnmount: PropTypes.func,
  getItems: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  listParams: PropTypes.instanceOf(Object).isRequired,
  setListParams: PropTypes.func.isRequired,
  action: PropTypes.instanceOf(Object),
  isHideSearch: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  hideRecordsInfo: PropTypes.bool,
  noBorders: PropTypes.bool,
  defaultParams: PropTypes.instanceOf(Object),
  title: PropTypes.string,
  dateRangeTypeOptions: PropTypes.instanceOf(Array),
  dateRangeType: PropTypes.string,
  showWithSearchOnly: PropTypes.bool,
  isCustomModalShown: PropTypes.bool,
  searchAutoFocus: PropTypes.bool.isRequired,
  isCalendarView: PropTypes.bool,
  defaultSortDesc: PropTypes.bool,
  optimizeParams: PropTypes.func,
  manual: PropTypes.bool.isRequired,
  defaultSort: PropTypes.instanceOf(Array).isRequired,
  searchPlaceholder: PropTypes.string,
  shouldShowItems: PropTypes.bool,
  datePicker: PropTypes.bool,
  tableDataType: PropTypes.func,
  onRowClick: PropTypes.func,
  rowStyles: PropTypes.instanceOf(Object),
  exportActions: PropTypes.instanceOf(Object),
  contextMenuId: PropTypes.string,
  todayCount: PropTypes.number,
  isDrawerOpen: PropTypes.bool.isRequired,
  usersCount: PropTypes.number,
  CustomInfoComponent: PropTypes.instanceOf(Object),
  SubComponent: PropTypes.func,
  withTimePicker: PropTypes.bool,
  chartMetadata: PropTypes.instanceOf(Object),
  expandedByDefault: PropTypes.instanceOf(Object),
  showTableRowsOnly: PropTypes.bool,
  shouldUpdate: PropTypes.bool,
  customActions: PropTypes.node,
  buildingLaunchDate: PropTypes.string,
  isMobileScreen: PropTypes.bool.isRequired,
  hasToggleFilters: PropTypes.bool,
};

export default AsyncTableComponent;
